import { entries, record, scalar, type Answers, type Entry } from "./lib/form-model";

const evidenceFields: Record<string, string> = {
  "pf.us.awards": "awards",
  "pf.us.compensation_records": "compensation_records",
  "pf.us.press": "press",
  "pf.us.referees": "referees",
  "pf.us.commercial_success": "commercial_success",
  "pf.us.union_membership": "union_membership",
};

type ProfessionalResponse = {
  career?: Record<string, unknown> | null;
  credits?: { credits?: Array<Record<string, string | null>> } | null;
  professional_evidence?: Record<string, Array<Record<string, string | null>>> | null;
  support_experience?: { support_experience?: string | null } | null;
  qualification?: { qualification_certificate?: { answer?: string | null; details?: string | null } | null } | null;
};

function pageFor(ids: string[]) {
  if (ids.includes("pf.career.occupation")) return "career";
  if (ids.includes("pf.career.credits")) return "credits";
  if (ids.includes("pf.us.o2_support")) return "support_experience";
  if (ids.includes("pf.it.qualification_certificate")) return "qualification";
  return "professional_evidence";
}

function blankToNull(value: string | undefined) {
  const text = (value || "").trim();
  return text || null;
}

export function professionalProfilePayload(ids: string[], answers: Answers) {
  const page = pageFor(ids);
  const body: Record<string, unknown> = { page };
  if (page === "career") {
    body.occupation = scalar(answers["pf.career.occupation"]).trim() || null;
    body.year_started = scalar(answers["pf.career.year_started"]).trim() || null;
    body.biography = scalar(answers["pf.career.biography"]).trim() || null;
  } else if (page === "credits") {
    body.credits = entries(answers["pf.career.credits"]).map((item) => ({
      production_title: item.production_title || null,
      role: item.role || null,
      production_type: item.production_type || null,
      production_type_other: blankToNull(item.production_type_other),
      year: blankToNull(item.year),
      director_or_producer: blankToNull(item.director_or_producer),
      source_url: blankToNull(item.source_url),
      notes: blankToNull(item.notes),
    }));
  } else if (page === "support_experience") {
    body.support_experience = scalar(answers["pf.us.o2_support"]).trim() || null;
  } else if (page === "qualification") {
    const detail = record(answers["pf.it.qualification_certificate"]);
    body.qualification_certificate = {
      answer: detail.value || scalar(answers["pf.it.qualification_certificate"]) || null,
      details: detail.explanation || null,
    };
  } else {
    for (const id of ids) {
      const key = evidenceFields[id];
      if (!key) continue;
      body[key] = entries(answers[id]).map((item) => Object.fromEntries(
        Object.entries(item).map(([field, value]) => [field, blankToNull(value)]),
      ));
    }
  }
  return body;
}

function putText(next: Answers, page: Record<string, unknown> | null | undefined, key: string, id: string) {
  const value = page?.[key];
  if (typeof value === "string" && value.trim()) next[id] = value;
}

/** Map a synthesize API payload onto career answer ids for the review screen. */
export function answersFromSynthesizedProfile(data: {
  biography?: string | null;
  occupation?: string | null;
  year_started?: string | null;
  credits?: Array<Record<string, string | null>> | null;
} | null | undefined): Answers {
  const next: Answers = {};
  putText(next, data, "occupation", "pf.career.occupation");
  putText(next, data, "year_started", "pf.career.year_started");
  putText(next, data, "biography", "pf.career.biography");
  if (data?.credits?.length) {
    next["pf.career.credits"] = data.credits.map((item) => ({
      production_title: item.production_title || "",
      role: item.role || "",
      production_type: item.production_type || "",
      production_type_other: "",
      year: item.year || "",
      director_or_producer: item.director_or_producer || "",
      source_url: item.credit_link || item.source_url || "",
      notes: item.notes || "",
    })) as Entry[];
  }
  return next;
}

export function answersFromProfessionalProfile(data: ProfessionalResponse | null | undefined): Answers {
  const next: Answers = {};
  const career = data?.career;
  putText(next, career, "occupation", "pf.career.occupation");
  putText(next, career, "year_started", "pf.career.year_started");
  putText(next, career, "biography", "pf.career.biography");
  const credits = data?.credits?.credits;
  if (credits?.length) {
    next["pf.career.credits"] = credits.map((item) => Object.fromEntries(
      Object.entries(item).map(([field, value]) => [field, value || ""]),
    )) as Entry[];
  }
  const evidence = data?.professional_evidence;
  for (const [id, key] of Object.entries(evidenceFields)) {
    const items = evidence?.[key];
    if (!items?.length) continue;
    next[id] = items.map((item) => Object.fromEntries(
      Object.entries(item).map(([field, value]) => [field, value || ""]),
    )) as Entry[];
  }
  putText(next, data?.support_experience, "support_experience", "pf.us.o2_support");
  const qualification = data?.qualification?.qualification_certificate;
  if (qualification?.answer || qualification?.details) {
    next["pf.it.qualification_certificate"] = { value: qualification.answer || "", explanation: qualification.details || "" };
  }
  return next;
}
